<script setup lang="ts">
/** [#e03fdbc2] Общая оболочка лендинга и статей: шапка, переключатели темы и языка, подвал. */
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { Languages, Moon, ShieldCheck, Sun } from 'lucide-vue-next';
import { CONTACT_EMAIL, PUBLIC_LOCALES, TELEGRAM_HANDLE, TELEGRAM_URL, localizedPath, splitLocale, type PublicLocale } from '@/seo/site';
import SiteNotice from './SiteNotice.vue';
import { usePublicPage } from './usePublicPage';
import '@fontsource-variable/onest/wght.css';
import './landing.css';

const { locale, copy, more, to } = usePublicPage();
const route = useRoute();

const LABELS: Record<PublicLocale, string> = { en: 'English', ru: 'Русский', uz: "O'zbekcha", es: 'Español' };
const languages = computed(() =>
  PUBLIC_LOCALES.map((code) => ({ code, label: LABELS[code], href: localizedPath(splitLocale(route.path).path, code) })),
);

/**
 * Якорь в меню. Блок на этой странице: прокручиваем сами, потому что роутер игнорирует повторный
 * клик по тому же адресу. Блока нет: обычный переход на главную, браузер сам найдёт якорь.
 */
function jump(event: MouseEvent, id: string): void {
  const target = document.getElementById(id);
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth' });
  history.replaceState(history.state, '', `#${id}`);
}

function openConsent(): void {
  window.dispatchEvent(new Event('lp-consent-open'));
}

function rememberLanguage(code: PublicLocale): void {
  try {
    localStorage.setItem('jan-mdm:lang', code);
  } catch {
    /* приватный режим: язык всё равно задаёт адрес */
  }
}

// Иконку темы показывает CSS по атрибуту, поэтому готовый HTML не зависит от выбора посетителя.
const theme = ref<'light' | 'dark'>('light');
onMounted(() => {
  theme.value = document.documentElement.getAttribute('data-lp-theme') === 'dark' ? 'dark' : 'light';
});
function toggleTheme(): void {
  theme.value = theme.value === 'dark' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-lp-theme', theme.value);
  try {
    localStorage.setItem('dwish:lp-theme', theme.value);
  } catch {
    /* приватный режим: выбор живёт до перезагрузки */
  }
}
</script>

<template>
  <div class="lp">
    <header class="lp-nav">
      <div class="lp-nav-bar">
        <RouterLink :to="to('/')" class="lp-brand">
          <img src="/logo-mark.png" alt="DWISH MDM" width="34" height="34" />
          <span>DWISH MDM</span>
        </RouterLink>
        <nav class="lp-nav-links" :aria-label="copy.nav.how">
          <a :href="to('/') + '#devices'" @click="jump($event, 'devices')">{{ more.nav.devices }}</a>
          <a :href="to('/') + '#lock'" @click="jump($event, 'lock')">{{ copy.nav.lock }}</a>
          <a :href="to('/') + '#price'" @click="jump($event, 'price')">{{ more.nav.price }}</a>
          <RouterLink :to="to('/partners')">{{ more.nav.partners }}</RouterLink>
          <RouterLink :to="to('/articles')">{{ copy.nav.articles }}</RouterLink>
        </nav>
        <div class="lp-nav-tools">
          <details class="lp-lang">
            <summary :aria-label="`${copy.nav.language}: ${locale.toUpperCase()}`"><Languages :size="17" /><span>{{ locale.toUpperCase() }}</span></summary>
            <ul>
              <li v-for="l in languages" :key="l.code">
                <a :href="l.href" :hreflang="l.code" :aria-current="l.code === locale ? 'true' : undefined" @click="rememberLanguage(l.code)">{{ l.label }}</a>
              </li>
            </ul>
          </details>
          <button type="button" class="lp-icon-btn" :aria-label="copy.nav.theme" @click="toggleTheme">
            <Sun class="lp-sun" :size="17" /><Moon class="lp-moon" :size="17" />
          </button>
          <a href="/login" class="lp-btn lp-btn--small">{{ copy.nav.login }}</a>
        </div>
      </div>
    </header>

    <slot />

    <footer class="lp-footer">
      <div class="lp-wrap lp-footer-grid">
        <div>
          <div class="lp-brand"><img src="/logo-mark.png" alt="" width="34" height="34" /><span>DWISH MDM</span></div>
          <p>{{ copy.footer.tagline }}</p>
          <a :href="TELEGRAM_URL" rel="noopener">Telegram {{ TELEGRAM_HANDLE }}</a>
          <a :href="`mailto:${CONTACT_EMAIL}`">{{ CONTACT_EMAIL }}</a>
        </div>
        <nav :aria-label="copy.footer.product">
          <h2>{{ copy.footer.product }}</h2>
          <a :href="to('/') + '#devices'" @click="jump($event, 'devices')">{{ more.nav.devices }}</a>
          <a :href="to('/') + '#price'" @click="jump($event, 'price')">{{ more.nav.price }}</a>
          <RouterLink :to="to('/abm')">{{ more.services.abm.title }}</RouterLink>
          <RouterLink :to="to('/partners')">{{ more.nav.partners }}</RouterLink>
          <RouterLink :to="to('/articles')">{{ copy.nav.articles }}</RouterLink>
          <RouterLink :to="to('/contract')">{{ more.contract.title }}</RouterLink>
          <RouterLink :to="to('/privacy')">{{ more.privacy.title }}</RouterLink>
          <button type="button" class="lp-footer-link" @click="openConsent">{{ more.notice.settings }}</button>
          <a href="/login">{{ copy.nav.login }}</a>
        </nav>
        <div>
          <h2>{{ copy.footer.requisites }}</h2>
          <dl>
            <dt>{{ copy.footer.company }}</dt><dd />
            <dt>{{ copy.footer.inn }}</dt><dd>313407362</dd>
            <dt>{{ copy.footer.phone }}</dt><dd><a href="tel:+998333330202">+998 33 333 02 02</a></dd>
            <dt>{{ copy.footer.account }}</dt><dd>20208000707554374001</dd>
            <dt>{{ copy.footer.mfo }}</dt><dd>00083</dd>
          </dl>
        </div>
      </div>
      <div class="lp-wrap lp-legal">
        <p>{{ copy.footer.legal }}</p>
        <div><span>{{ copy.footer.rights }}</span><span class="lp-city"><ShieldCheck :size="13" />{{ copy.footer.city }}</span></div>
      </div>
    </footer>
    <SiteNotice />
  </div>
</template>
